// Floating install banner — appears at the bottom of the screen on mobile
// when the user is in a regular browser (not already in the installed PWA).
// On Android Chrome it captures the native beforeinstallprompt event so a single
// tap on "Install" triggers the OS prompt. On iOS Safari (which doesn't support
// beforeinstallprompt) it routes to /download for step-by-step instructions.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, X, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const DISMISSED_KEY = "hansa_install_banner_dismissed_v1";

export const InstallBanner = () => {
  const { t } = useLang();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed (running as PWA)? Hide forever.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Dismissed in this session? Hide.
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    // Only nag on small screens — desktop users already see the header link.
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    // Show after a short delay so we don't get in the way of first paint.
    const timer = setTimeout(() => setVisible(true), 1500);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const onInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        /* user cancelled — ignore */
      }
      setDeferredPrompt(null);
      dismiss();
    }
    // If no native prompt available (iOS), the link below routes to /download.
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-banner"
      className="fixed bottom-16 left-3 right-3 z-50 md:hidden cd-fade-up"
    >
      <div className="bg-[#1B4332] text-white rounded-2xl shadow-2xl border border-[#1B4332] p-3 flex items-center gap-3">
        <div className="relative shrink-0">
          <img
            src="/founder-sanket.jpeg"
            alt="Sanket — Founder, Ramkishan Agri Innovate"
            className="w-12 h-12 rounded-full object-cover border-2 border-[#D4A373]"
          />
          <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#D4A373] flex items-center justify-center">
            <Smartphone className="w-2.5 h-2.5 text-[#1C1C1C]" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-[#D4A373] font-bold">
            {t("installBanner.endorsement")}
          </div>
          <div className="font-bold text-sm leading-tight mt-0.5">{t("installBanner.title")}</div>
          <div className="text-xs text-white/80 leading-snug mt-0.5">{t("installBanner.subtitle")}</div>
        </div>
        {deferredPrompt ? (
          <button
            data-testid="install-banner-install"
            onClick={onInstall}
            className="shrink-0 bg-[#D4A373] hover:bg-[#c89460] text-[#1C1C1C] font-bold text-sm px-4 py-2.5 rounded-full flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> {t("installBanner.install")}
          </button>
        ) : (
          <Link
            to="/download"
            data-testid="install-banner-learn"
            onClick={dismiss}
            className="shrink-0 bg-[#D4A373] hover:bg-[#c89460] text-[#1C1C1C] font-bold text-sm px-4 py-2.5 rounded-full flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> {t("installBanner.install")}
          </Link>
        )}
        <button
          data-testid="install-banner-dismiss"
          onClick={dismiss}
          className="shrink-0 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-white/80" />
        </button>
      </div>
    </div>
  );
};
